import { Interface } from '@ethersproject/abi'
import { useWeb3React } from '@web3-react/core'
import { Currency, CurrencyAmount, Token } from '@x-swap-protocol/sdk-core'
import ERC20ABI from 'abis/erc20.json'
import { Erc20Interface } from 'abis/types/Erc20'
import JSBI from 'jsbi'
import { useMultipleContractSingleData, useSingleContractMultipleData } from 'lib/hooks/multicall'
import { useEffect, useMemo, useRef, useState } from 'react'

import { nativeOnChain } from '../../constants/tokens'
import { useInterfaceMulticall } from '../../hooks/useContract'
import { isAddress } from '../../utils'

/**
 * Returns a map of the given addresses to their eventually consistent ETH balances.
 */
export function useNativeCurrencyBalances(uncheckedAddresses?: (string | undefined)[]): {
  [address: string]: CurrencyAmount<Currency> | undefined
} {
  const { chainId } = useWeb3React()
  const multicallContract = useInterfaceMulticall()

  const validAddressInputs: [string][] = useMemo(
    () =>
      uncheckedAddresses
        ? uncheckedAddresses
            .map(isAddress)
            .filter((a): a is string => a !== false)
            .sort()
            .map((addr) => [addr])
        : [],
    [uncheckedAddresses]
  )

  const results = useSingleContractMultipleData(multicallContract, 'getEthBalance', validAddressInputs)

  return useMemo(
    () =>
      validAddressInputs.reduce<{ [address: string]: CurrencyAmount<Currency> }>((memo, [address], i) => {
        const value = results?.[i]?.result?.[0]
        if (value && chainId)
          memo[address] = CurrencyAmount.fromRawAmount(nativeOnChain(chainId), JSBI.BigInt(value.toString()))
        return memo
      }, {}),
    [validAddressInputs, chainId, results]
  )
}

const ERC20Interface = new Interface(ERC20ABI) as Erc20Interface
const TOKEN_BALANCE_LOADING_TIMEOUT_MS = 5_000
const tokenBalancesGasRequirement = { blocksPerFetch: 1, gasRequired: 1_000_000 }

/**
 * Returns a map of token addresses to their eventually consistent token balances for a single account.
 */
export function useTokenBalancesWithLoadingIndicator(
  address?: string,
  tokens?: (Token | undefined)[]
): [{ [tokenAddress: string]: CurrencyAmount<Token> | undefined }, boolean] {
  const { chainId } = useWeb3React() // we cannot fetch balances cross-chain
  const validatedTokens: Token[] = useMemo(
    () => tokens?.filter((t?: Token): t is Token => isAddress(t?.address) !== false && t?.chainId === chainId) ?? [],
    [chainId, tokens]
  )
  const validatedTokenAddresses = useMemo(() => validatedTokens.map((vt) => vt.address), [validatedTokens])

  const balances = useMultipleContractSingleData(
    validatedTokenAddresses,
    ERC20Interface,
    'balanceOf',
    useMemo(() => [address], [address]),
    tokenBalancesGasRequirement
  )

  const anyLoading: boolean = useMemo(() => balances.some((callState) => callState.loading), [balances])
  const anyError: boolean = useMemo(() => balances.some((callState) => callState.error), [balances])
  const balanceRequestKey = useMemo(
    () => `${chainId ?? ''}:${address ?? ''}:${validatedTokenAddresses.join(',')}`,
    [address, chainId, validatedTokenAddresses]
  )
  const [loadingTimedOut, setLoadingTimedOut] = useState(false)
  const lastSuccessfulBalances = useRef<{
    key: string
    balances: { [tokenAddress: string]: CurrencyAmount<Token> | undefined }
  }>({ key: '', balances: {} })

  useEffect(() => {
    setLoadingTimedOut(false)
    if (!anyLoading) return undefined

    const timeout = setTimeout(() => setLoadingTimedOut(true), TOKEN_BALANCE_LOADING_TIMEOUT_MS)
    return () => clearTimeout(timeout)
  }, [anyLoading, balanceRequestKey])

  const parsedBalances = useMemo(
    () =>
      address && validatedTokens.length > 0
        ? validatedTokens.reduce<{ [tokenAddress: string]: CurrencyAmount<Token> | undefined }>((memo, token, i) => {
            const value = balances?.[i]?.result?.[0]
            const amount = value ? JSBI.BigInt(value.toString()) : undefined
            if (amount) {
              memo[token.address] = CurrencyAmount.fromRawAmount(token, amount)
            }
            return memo
          }, {})
        : {},
    [address, balances, validatedTokens]
  )

  useEffect(() => {
    if (!anyLoading && !anyError) {
      lastSuccessfulBalances.current = {
        key: balanceRequestKey,
        balances: parsedBalances,
      }
    }
  }, [anyError, anyLoading, balanceRequestKey, parsedBalances])

  return useMemo(() => {
    const cachedBalances =
      lastSuccessfulBalances.current.key === balanceRequestKey ? lastSuccessfulBalances.current.balances : {}
    const mergedBalances = anyLoading || anyError ? { ...cachedBalances, ...parsedBalances } : parsedBalances
    const hasUsableBalances = Object.keys(mergedBalances).length > 0
    const shouldKeepLoading = anyLoading && !anyError && !loadingTimedOut && !hasUsableBalances

    return [mergedBalances, shouldKeepLoading]
  }, [anyError, anyLoading, balanceRequestKey, loadingTimedOut, parsedBalances])
}

export function useTokenBalances(
  address?: string,
  tokens?: (Token | undefined)[]
): { [tokenAddress: string]: CurrencyAmount<Token> | undefined } {
  return useTokenBalancesWithLoadingIndicator(address, tokens)[0]
}

// get the balance for a single token/account combo
export function useTokenBalance(account?: string, token?: Token): CurrencyAmount<Token> | undefined {
  const tokenBalances = useTokenBalances(
    account,
    useMemo(() => [token], [token])
  )
  if (!token) return undefined
  return tokenBalances[token.address]
}

export function useCurrencyBalances(
  account?: string,
  currencies?: (Currency | undefined)[]
): (CurrencyAmount<Currency> | undefined)[] {
  const tokens = useMemo(
    () => currencies?.filter((currency): currency is Token => currency?.isToken ?? false) ?? [],
    [currencies]
  )

  const { chainId } = useWeb3React()
  const tokenBalances = useTokenBalances(account, tokens)
  const containsETH: boolean = useMemo(() => currencies?.some((currency) => currency?.isNative) ?? false, [currencies])
  const ethBalance = useNativeCurrencyBalances(useMemo(() => (containsETH ? [account] : []), [containsETH, account]))

  return useMemo(
    () =>
      currencies?.map((currency) => {
        if (!account || !currency || currency.chainId !== chainId) return undefined
        if (currency.isToken) return tokenBalances[currency.address]
        if (currency.isNative) return ethBalance[account]
        return undefined
      }) ?? [],
    [account, chainId, currencies, ethBalance, tokenBalances]
  )
}

export default function useCurrencyBalance(
  account?: string,
  currency?: Currency
): CurrencyAmount<Currency> | undefined {
  return useCurrencyBalances(
    account,
    useMemo(() => [currency], [currency])
  )[0]
}
